import SectionPanel from '@/components/SectionPanel';
import { useOrganization } from "src/auth/AuthContext";
import { SimpleGrid, Textarea } from "@chakra-ui/react";
import type { JSX } from 'react';
import ErrorMessage from "src/components/ErrorMessage";
import { useForm } from "react-hook-form";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fund } from 'src/api/Fund';
import DateSelectController from '@/components/fields/DateSelect';
import { useProjectNames } from '@/hooks/useProjectNames';
import { fundService } from "src/service/FundService";
import CancelButton from "src/components/buttons/CancelButton";
import SubmitButton from "@/components/buttons/SubmitButton";
import { Field } from "@/components/ui/field";
import { AmountInputController } from "@/components/fields/AmountInput";
import { ProjectSelectController } from "@/components/fields/ProjectSelect";
import BudgetYearController from "@/components/fields/BudgetYearController";
import { convert, DonationCodeController, DonationCodeOption } from "@/components/fields/DonationCodeSelect";
import { donationsCodeService } from "@/service/DonationCodeService";
import FundSourceController from "@/components/fields/FundSourceController";
import FormPage from '@/components/FormPage';
import { useI18n } from '@/i18n/I18nContext';

export interface FundsFormProps {
    fundId?: number;
    organizationId: number;
    onSubmit: (entry: Fund) => Promise<void>;
    viewOnly?: boolean;
    header: string;
}

const FundsForm = ({ fundId, organizationId, onSubmit, viewOnly, header }: FundsFormProps): JSX.Element => {
    const { t } = useI18n();
    const organization = useOrganization();
    const { control, register, handleSubmit, reset, formState: { isSubmitting } } = useForm<Fund>({
        defaultValues: {
            id: fundId,
            fundDate: new Date(),
            budgetYear: new Date().getFullYear(),
            projectId: -1,
            donationCode: '',
            amount: 0,
            comments: '',
            fundSourceId: null,
            fundSourceName: '',
            organizationId
        }
    });
    const [serverError, setServerError] = useState('');
    const { options: projectNames } = useProjectNames();
    const [donationCodes, setDonationCodes] = useState<DonationCodeOption[]>([]);
    const [institutions, setInstitutions] = useState<string[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        if ((fundId ?? -1) > -1) {
            fundService.find(fundId).then(p => reset(p));
        }
    }, [fundId, reset]);

    useEffect(() => {
        const codes = donationsCodeService.listAll().then(r => setDonationCodes(convert(r)));
        const sourceNames = fundService.fundSourceNames().then(setInstitutions);
        Promise.all([codes, sourceNames]);
    }, []);

    const readOnly = viewOnly ?? false;

    const onSubmitInternal = (entry: Fund): Promise<void> => {
        const payload: Fund = {
            ...entry,
            fundSourceId: entry.fundSourceId ?? null,
            organizationId
        };
        return onSubmit(payload)
            .then(() => navigate('/funds'))
            .catch(() => setServerError('Fehler'));
    };

    return (
        <FormPage title={header} description={t('forms.funds.description')}
            onSubmit={handleSubmit(onSubmitInternal)} invalid={!!serverError}
            actions={<>
                <CancelButton onClick={() => navigate('/funds/')} />
                {!readOnly && <SubmitButton isSubmitting={isSubmitting} />}
            </>}>
            {serverError.length > 0 && <ErrorMessage message={serverError} />}
            <SectionPanel title={t('forms.sections.assignment')}>
                <ProjectSelectController control={control} projectNames={projectNames} name='projectId' label='Projekt' readOnly={readOnly} required />

                <FundSourceController
                    control={control}
                    name='fundSourceName'
                    label='Institution (oder neue Institution erstellen)'
                    readOnly={readOnly}
                    options={institutions}
                    required
                />

                <SimpleGrid columns={{ base: 1, md: 2 }} gap='4'>
                    <BudgetYearController control={control} name='budgetYear' label='Fonds Jahr' readOnly={readOnly} required />

                    <DonationCodeController
                        control={control}
                        codes={donationCodes}
                        name='donationCode'
                        label='Kostenstelle'
                        readOnly={readOnly}
                        required
                    />
                </SimpleGrid>
            </SectionPanel>
            <SectionPanel title={t('forms.sections.amountAndDate')}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap='4'>
                    <DateSelectController label='Datum' control={control} name='fundDate' clearable={false} required disabled={readOnly} />
                    <AmountInputController control={control} name='amount' label={`Summe (${organization?.currency ?? '—'})`} readOnly={readOnly} />
                </SimpleGrid>
                <Field id="comments" label='Bemerkungen' disabled={readOnly}>
                    <Textarea {...register('comments')} minH='7rem' resize='vertical' />
                </Field>

            </SectionPanel>
        </FormPage>
    );
}

export default FundsForm;
